/* RC 1.52 Phase 3B.3 — Standalone Interview Prep polling helper
 * (operator-locked, 2026-05-11 PM, REVISED to be recovery-aware).
 *
 * Mirror of `coverLetterPolling.js`. Interview Prep is a first-class
 * action with its own backend route family (`POST/GET /api/interview-prep`).
 *
 * Recovery semantics (the locked rule):
 *   NO SUCCESSFUL DELIVERABLE → NO CHARGE.
 *   REPLAY / RECOVERY / HYDRATION → NO NEW CHARGE.
 *
 * This helper:
 *   1. POSTs to /api/interview-prep/generate.
 *   2. On 200 sync → returns the canonical envelope (which IS the
 *      result block).
 *   3. On 202 async → invokes `onRunIdReady(runId)` IMMEDIATELY so
 *      the caller can write `?interview_prep_id={runId}` synchronously,
 *      then polls /api/interview-prep/{id} until terminal.
 *   4. On signal abort / hard-cap timeout → throws an error with
 *      `recoverable: true` (NOT 'AbortError'). The caller surfaces
 *      a calm "still generating in the background" toast rather
 *      than a hard "aborted" failure.
 *
 * Header gate: every request sends `X-Envelope: rc_1_52_canonical`
 * so sync and poll lanes return the same canonical shape.
 */

import { apiFetch } from './api';

const POLL_INTERVAL_MS  = 1500;
const POLL_HARD_CAP_MS  = 120_000;
const HEADERS_CANONICAL = { 'X-Envelope': 'rc_1_52_canonical' };

export class InterviewPrepPollingError extends Error {
  constructor(message, {
    recoverable = false, code, detail, status, runId,
  } = {}) {
    super(message);
    this.recoverable = recoverable;
    this.code        = code;
    this.detail      = detail;
    this.status      = status;
    this.runId       = runId;
  }
}

function _makeRecoverable(message, extra = {}) {
  return new InterviewPrepPollingError(message, {
    recoverable: true,
    ...extra,
  });
}

async function _sleep(ms, { signal } = {}) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(_makeRecoverable(
        'Interview Prep generation is still running in the background.',
        { code: 'interview_prep_aborted_recoverable' },
      ));
    }
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(_makeRecoverable(
        'Interview Prep generation is still running in the background.',
        { code: 'interview_prep_aborted_recoverable' },
      ));
    }, { once: true });
  });
}

/**
 * Poll an existing run id until it reaches a terminal state. Exposed
 * so the section can recover by run_id after an unmount / refresh.
 *
 * @param {string} runId
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal]
 * @returns {Promise<object>} canonical result block on complete.
 *                            Throws InterviewPrepPollingError on
 *                            failure / recoverable abort / timeout.
 */
export async function pollInterviewPrepUntilTerminal(runId, { signal } = {}) {
  if (!runId) throw new Error('pollInterviewPrepUntilTerminal: runId required');
  const startedAt = Date.now();
  while (true) {
    if (signal?.aborted) {
      throw _makeRecoverable(
        'Interview Prep generation is still running in the background.',
        { code: 'interview_prep_aborted_recoverable', runId },
      );
    }
    if (Date.now() - startedAt > POLL_HARD_CAP_MS) {
      throw _makeRecoverable(
        'Interview Prep is taking longer than usual. We will surface it shortly.',
        { code: 'interview_prep_still_running', runId },
      );
    }
    let env;
    try {
      // eslint-disable-next-line no-await-in-loop
      env = await apiFetch(`/api/interview-prep/${runId}`, {
        method:  'GET',
        headers: HEADERS_CANONICAL,
        signal,
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw _makeRecoverable(
          'Interview Prep generation is still running in the background.',
          { code: 'interview_prep_aborted_recoverable', runId },
        );
      }
      // Network blip — keep polling.
      // eslint-disable-next-line no-await-in-loop
      await _sleep(POLL_INTERVAL_MS, { signal });
      continue;
    }
    const status = (env?.status || '').toLowerCase();
    if (status === 'complete') {
      // The poll endpoint returns the canonical envelope with the
      // result block on `env.result`. Surface a shape that mirrors
      // the sync_200 dispatch envelope (which IS the result block).
      const r = env.result || {};
      return {
        ...r,
        run_id:            env.run_id || runId,
        interview_prep_id: r.interview_prep_id || env.run_id || runId,
        status:            'complete',
        served_via:        'poll_200',
        billing:           env.billing || r.billing || null,
        idempotent_replay: false,
      };
    }
    if (status === 'failed') {
      throw new InterviewPrepPollingError(
        env?.error_detail
        || 'Interview Prep generation failed. Please try again.',
        {
          recoverable: !!env?.retryable,
          code:        env?.error_code,
          detail:      env?.error_detail,
          runId,
        },
      );
    }
    // eslint-disable-next-line no-await-in-loop
    await _sleep(env?.retry_after_ms || POLL_INTERVAL_MS, { signal });
  }
}

/**
 * Submit a Workspace Interview Prep run and resolve with the
 * canonical result block. Recovery-aware: aborts and timeouts
 * surface as `recoverable=true` errors, never as fatal toasts.
 *
 * @param {object} params
 * @param {string} params.jobId
 * @param {string} params.jobText
 * @param {string} [params.sourceType]      'uploaded_cv' | 'cv_variant'
 * @param {string} [params.sourceId]
 * @param {string} [params.cvVariantId]
 * @param {string} [params.baseCvId]
 * @param {string} [params.title]
 * @param {string} [params.idempotencyKey]  Stable across retries;
 *                                          omit to force-regenerate.
 * @param {boolean}[params.forceRegenerate] Explicit force flag.
 * @param {(runId: string) => void} [params.onRunIdReady]
 *        Fires the moment the async_202 dispatch returns a run_id,
 *        BEFORE polling begins. Use this to write `?interview_prep_id`
 *        synchronously so refresh / tab-return rebinds.
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<object>} canonical result block.
 */
export async function generateInterviewPrepWithPolling({
  jobId,
  jobText,
  sourceType,
  sourceId,
  cvVariantId,
  baseCvId,
  title,
  idempotencyKey,
  forceRegenerate = false,
  onRunIdReady,
  signal,
} = {}) {
  if (!jobText) throw new Error('jobText is required.');

  const payload = {
    job_id:           jobId,
    job_text:         jobText,
    source_type:      sourceType,
    source_id:        sourceId,
    cv_variant_id:    cvVariantId,
    base_cv_id:       baseCvId,
    title:            title || '',
    idempotency_key:  forceRegenerate ? null : (idempotencyKey || null),
    force_regenerate: !!forceRegenerate,
  };

  // POST — fast-path returns the canonical envelope directly. On
  // slow-path the backend route emits a 202 JSON body; apiFetch
  // surfaces both as a parsed JSON object.
  let envelope;
  try {
    envelope = await apiFetch('/api/interview-prep/generate', {
      method:  'POST',
      body:    JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json', ...HEADERS_CANONICAL },
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw _makeRecoverable(
        'Interview Prep dispatch was interrupted — it may still be running.',
        { code: 'interview_prep_aborted_recoverable' },
      );
    }
    throw err;
  }

  const served = (envelope?.served_via || '').toLowerCase();
  if (served === 'sync_200' || served === 'replay_200') {
    // Sync envelope IS the canonical result block at the top level.
    return envelope;
  }

  // Async — surface the run_id immediately so the caller can write
  // the URL parameter and the in-flight marker before polling.
  const runId = envelope?.interview_prep_id || envelope?.run_id;
  if (!runId) {
    throw new Error('Interview Prep dispatch returned no run id.');
  }
  if (typeof onRunIdReady === 'function') {
    try { onRunIdReady(runId); } catch { /* noop */ }
  }
  return pollInterviewPrepUntilTerminal(runId, { signal });
}
